import type { ColumnDef } from "@tanstack/react-table";

import DataTable from "./DataTable";
import type { EmailChannel, IntegrationType, SourceAdapter } from "../lib/types";

/**
 * Was der Realm an Fremdsysteme angedockt hat (D-GOV-24), read-only (D-GOV-25).
 *
 * Zwei Eigenschaften tragen die Ansicht:
 *
 * 1. **Nicht lesbar ist nicht leer.** ``null`` heisst „nicht einsehbar", ``false``
 *    heisst „nicht gebunden" — ein gemeinsamer Falsy-Zweig gaebe einen
 *    Rechtefehler als leeren Zustand aus.
 * 2. **Der Katalog ist die Gegenprobe.** Ein ``adapter_type``, den die Instanz
 *    nicht (mehr) kennt, ist bindbar geblieben und pollt nie — das zeigt sich
 *    sonst erst als ausbleibender Ingest.
 * 3. **Gebunden heisst nicht abholend.** ``enabled`` traegt die Bindung,
 *    ``polling`` den automatischen Abruf; ein pausierter Abruf ist ein gewollter
 *    Zustand und gehoert deshalb in den Status, nicht in die Befund-Spalte.
 */
interface Props {
  adapters: SourceAdapter[] | null;
  catalog: IntegrationType[] | null;
  emailChannel: EmailChannel | null;
  /** true = 403/Fehler beim Lesen (nicht: „nicht gebunden"). */
  emailChannelUnreadable: boolean;
  teamsChannel: Record<string, unknown> | null;
}

export interface AdapterRow {
  name: string;
  type: string;
  typeLabel: string;
  state: string;
  credentials: string;
  problem: string;
}

const ADAPTER_COLS: ColumnDef<AdapterRow, any>[] = [
  { header: "Name", accessorKey: "name" },
  { header: "Typ", accessorKey: "typeLabel" },
  { header: "Status", accessorKey: "state" },
  { header: "Credential", accessorKey: "credentials" },
  { header: "Befund", accessorKey: "problem" },
];

export function toRows(
  adapters: SourceAdapter[],
  catalog: IntegrationType[] | null,
): AdapterRow[] {
  const sources = (catalog ?? []).filter((c) => c.kind === "source");
  // `null` = Katalog nicht lesbar. Eine leere Menge hiesse "kein Typ bekannt"
  // und wuerde jede Bindung als unbekannt melden (D-GOV-25).
  const known = catalog === null ? null : new Set(sources.map((c) => c.type_id));
  const label = new Map<string, string>(
    sources.map((c): [string, string] => [c.type_id, c.display_name]),
  );
  return adapters.map((a) => ({
    name: a.name,
    type: a.adapter_type,
    typeLabel: label.get(a.adapter_type) ?? a.adapter_type,
    state: !a.enabled ? "abgeschaltet" : a.polling ? "aktiv" : "aktiv, Abruf pausiert",
    credentials: a.has_credentials ? "hinterlegt" : "fehlt",
    problem:
      known !== null && !known.has(a.adapter_type)
        ? `Typ '${a.adapter_type}' ist dieser Instanz unbekannt`
        : !a.has_credentials
          ? "ohne Credential — pollt nicht"
          : "",
  }));
}

export default function IntegrationsSection({
  adapters,
  catalog,
  emailChannel,
  emailChannelUnreadable,
  teamsChannel,
}: Props) {
  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">
          Quellen{" "}
          <span className="normal-case text-kw-text-faint">— was Material hereinbringt</span>
        </h3>
        {adapters === null ? (
          <div className="text-xs text-kw-text-faint">
            Nicht einsehbar mit deinen Rechten.
          </div>
        ) : (
          <DataTable
            columns={ADAPTER_COLS}
            data={toRows(adapters, catalog)}
            emptyHint="Keine Quelle gebunden — dieser Realm wird ausschliesslich von Hand befuellt."
          />
        )}
      </div>

      <div className="space-y-2">
        <h3 className="text-sm uppercase tracking-wide text-kw-text-muted">
          Kanaele{" "}
          <span className="normal-case text-kw-text-faint">— wohin Rueckfragen gehen</span>
        </h3>
        <ul className="text-xs space-y-1.5 text-kw-text-primary">
          <li>
            <span className="font-mono text-kw-l4">email</span>{" "}
            {emailChannelUnreadable ? (
              <span className="text-kw-text-faint">
                nicht einsehbar — weder Owner noch Admin dieses Realms
              </span>
            ) : emailChannel ? (
              <span>
                gebunden · Token <code>{emailChannel.address_token}</code> ·{" "}
                {emailChannel.enabled ? "aktiv" : "abgeschaltet"}
              </span>
            ) : (
              <span className="text-kw-text-faint">nicht gebunden</span>
            )}
          </li>
          <li>
            <span className="font-mono text-kw-l4">teams</span>{" "}
            {teamsChannel ? (
              <span>
                gebunden · team <code>{String(teamsChannel.team_id ?? "?")}</code> · channel{" "}
                <code>{String(teamsChannel.channel_id ?? "?")}</code>
              </span>
            ) : (
              <span className="text-kw-text-faint">
                nicht gebunden — Meeting-Chats brauchen keine Bindung (aus dem Quell-Adapter
                abgeleitet)
              </span>
            )}
          </li>
        </ul>
      </div>
    </div>
  );
}
